import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ApiError, NetworkError } from '@/lib/api-client';
import { ASSETS } from '@/lib/config';
import { useAuthStore } from '@/stores/authStore';
import { STRINGS } from '@/strings/strings';

type ResendState = 'idle' | 'sending' | 'sent' | 'error';

export function EmailSentRoute() {
  const [searchParams] = useSearchParams();
  const userEmail = useAuthStore((s) => s.user?.email ?? null);
  const requestEmailVerification = useAuthStore((s) => s.requestEmailVerification);

  const email = searchParams.get('email') ?? userEmail;
  const [resendState, setResendState] = useState<ResendState>('idle');
  const [resendError, setResendError] = useState<string | null>(null);

  const body = email
    ? STRINGS.auth.emailSent.bodyWithEmail.replace('{email}', email)
    : STRINGS.auth.emailSent.bodyWithoutEmail;

  const handleResend = async () => {
    setResendError(null);
    setResendState('sending');
    try {
      await requestEmailVerification();
      setResendState('sent');
    } catch (err) {
      setResendState('error');
      if (err instanceof NetworkError) {
        setResendError(STRINGS.auth.errors.networkError);
      } else if (err instanceof ApiError) {
        setResendError(STRINGS.auth.errors.genericBackend);
      } else {
        setResendError(STRINGS.auth.errors.genericBackend);
      }
    }
  };

  return (
    <div className="text-center">
      <img
        src={ASSETS.kaptnKuschelImage}
        alt={STRINGS.onboarding.welcome.kaptnKuschelAlt}
        loading="eager"
        className="mx-auto mb-4 w-40 md:w-48 h-auto"
      />
      <h1 className="text-[22px] font-semibold text-dream-white mb-3">
        {STRINGS.auth.emailSent.title}
      </h1>
      <p className="text-sm text-dream-white/90 leading-relaxed mb-3">{body}</p>
      <p className="text-xs text-dream-white/60 mb-6">{STRINGS.auth.emailSent.spamHint}</p>

      {resendState === 'sent' ? (
        <div
          role="status"
          className="w-full h-12 flex items-center justify-center rounded-lg bg-white/5 border border-white/10 text-sm text-dream-white/90"
        >
          {STRINGS.auth.emailSent.resendConfirmation}
        </div>
      ) : (
        <Button
          type="button"
          onClick={handleResend}
          disabled={resendState === 'sending'}
          className="w-full h-12 text-base font-semibold"
        >
          {resendState === 'sending' && <Loader2 className="w-4 h-4 animate-spin" />}
          {resendState === 'sending'
            ? STRINGS.auth.emailSent.resendingState
            : STRINGS.auth.emailSent.resendButton}
        </Button>
      )}

      {resendError && (
        <p role="alert" className="mt-3 text-xs text-[#FCA5A5]">
          {resendError}
        </p>
      )}
    </div>
  );
}
